import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { PhotoUploader } from '@nargus/shared';
import {
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Download,
  Info,
  LoaderCircle,
  Pause,
  Play,
  Star,
  Trash2,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useBlobUrl } from '@/hooks/use-blob-url';
import { useErrorText } from '@/lib/error-text';
import { formatSize } from '@/lib/files';
import { formatDimensions, formatDuration } from '@/lib/media';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { GridPhoto } from './photo-grid';

const SLIDESHOW_MS = 4000;
const ZOOM_STEP = 0.5;
const ZOOM_MAX = 5;
const SWIPE_PX = 50;

const WHEN = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'long',
  timeStyle: 'short',
});

export interface LightboxPhoto extends GridPhoto {
  size: number;
  width?: number;
  height?: number;
  uploader?: PhotoUploader;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function PhotoLightbox<T extends LightboxPhoto>({
  photos,
  index,
  onIndex,
  onClose,
  load,
  onToggleFavorite,
  onSaveCaption,
  onDelete,
  canModify,
}: {
  photos: T[];
  index: number;
  onIndex: (next: number) => void;
  onClose: () => void;
  load: (photo: T) => Promise<Blob>;
  onToggleFavorite?: (photo: T) => void;
  onSaveCaption?: (photo: T, caption: string) => void;
  onDelete?: (photo: T) => void;
  canModify?: (photo: T) => boolean;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const photo = photos[index];
  const total = photos.length;

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showInfo, setShowInfo] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [caption, setCaption] = useState<string | null>(null);

  const loader = useRef(load);
  loader.current = load;

  const { url, error } = useBlobUrl(photo?.id ?? '', () => loader.current(photo!));

  const go = useCallback(
    (delta: number) => {
      if (total === 0) return;
      onIndex((index + delta + total) % total);
    },
    [index, total, onIndex],
  );

  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setCaption(null);
  }, [index]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    if (!playing || total < 2) return;
    const timer = window.setInterval(() => go(1), SLIDESHOW_MS);
    return () => window.clearInterval(timer);
  }, [playing, total, go]);

  useEffect(() => {
    const next = photos[index + 1];
    const previous = photos[index - 1];

    for (const neighbour of [next, previous]) {
      if (neighbour) void loader.current(neighbour).catch(() => undefined);
    }
  }, [index, photos]);

  const editing = caption !== null;
  const favoriteRef = useRef(onToggleFavorite);
  favoriteRef.current = onToggleFavorite;

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (editing) return;

      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          go(-1);
          break;
        case 'ArrowRight':
          go(1);
          break;
        case ' ':
          event.preventDefault();
          setPlaying((current) => !current);
          break;
        case 'i':
          setShowInfo((current) => !current);
          break;
        case 'f':
          if (photo) favoriteRef.current?.(photo);
          break;
        default:
          break;
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, go, onClose, photo]);

  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const drag = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  if (!photo) return null;

  const label = photo.caption ?? photo.filename;
  const isImage = photo.kind === 'image';
  const mayModify = canModify?.(photo) ?? true;

  function setZoomLevel(next: number) {
    const level = clamp(next, 1, ZOOM_MAX);
    setZoom(level);
    if (level === 1) setPan({ x: 0, y: 0 });
  }

  function onWheel(event: ReactWheelEvent) {
    if (!isImage) return;
    setZoomLevel(zoom + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
  }

  function onTouchStart(event: ReactTouchEvent) {
    if (zoom > 1) return;
    const touch = event.touches[0];
    if (touch) touchStart.current = { x: touch.clientX, y: touch.clientY };
  }

  function onTouchEnd(event: ReactTouchEvent) {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || zoom > 1) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) > SWIPE_PX && Math.abs(deltaX) > Math.abs(deltaY)) {
      go(deltaX < 0 ? 1 : -1);
    }
  }

  function onPointerDown(event: ReactPointerEvent) {
    if (zoom === 1) return;
    drag.current = { x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: ReactPointerEvent) {
    const start = drag.current;
    if (!start) return;
    setPan({
      x: start.panX + (event.clientX - start.x),
      y: start.panY + (event.clientY - start.y),
    });
  }

  function onPointerUp() {
    drag.current = null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-50 flex flex-col bg-black/95 text-white"
    >
      <header className="flex items-center gap-1 p-2">
        <span className="px-2 text-sm tabular-nums text-white/70">
          {index + 1} / {total}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-white/70">{label}</span>

        {isImage && (
          <>
            <IconButton
              label={t('photos.zoomOut')}
              disabled={zoom <= 1}
              onClick={() => setZoomLevel(zoom - ZOOM_STEP)}
            >
              <ZoomOut />
            </IconButton>
            <IconButton
              label={t('photos.zoomIn')}
              disabled={zoom >= ZOOM_MAX}
              onClick={() => setZoomLevel(zoom + ZOOM_STEP)}
            >
              <ZoomIn />
            </IconButton>
          </>
        )}

        {total > 1 && (
          <IconButton
            label={playing ? t('photos.slideshowStop') : t('photos.slideshowStart')}
            onClick={() => setPlaying(!playing)}
          >
            {playing ? <Pause /> : <Play />}
          </IconButton>
        )}

        {onToggleFavorite && (
          <IconButton
            label={photo.favorite ? t('photos.unfavorite') : t('photos.favorite')}
            onClick={() => onToggleFavorite(photo)}
          >
            <Star className={photo.favorite ? 'fill-white' : undefined} />
          </IconButton>
        )}

        <IconButton label={t('photos.info')} onClick={() => setShowInfo(!showInfo)}>
          <Info />
        </IconButton>

        {url && (
          <a
            href={url}
            download={photo.filename}
            aria-label={t('common.download')}
            className="inline-flex size-9 items-center justify-center rounded-md text-white/80 hover:bg-white/10 hover:text-white [&_svg]:size-4"
          >
            <Download />
          </a>
        )}

        {onDelete && mayModify && (
          <IconButton label={t('common.delete')} onClick={() => onDelete(photo)}>
            <Trash2 />
          </IconButton>
        )}

        <IconButton label={t('common.close')} onClick={onClose}>
          <X />
        </IconButton>
      </header>

      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden"
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {error ? (
          <p className="flex items-center gap-1.5 text-sm text-red-300">
            <CircleAlert className="size-4 shrink-0" />
            {errorText(error)}
          </p>
        ) : !url ? (
          <LoaderCircle className="size-6 animate-spin text-white/70" />
        ) : photo.kind === 'video' ? (
          <video
            key={photo.id}
            src={url}
            controls
            autoPlay
            playsInline
            className="max-h-full max-w-full"
          />
        ) : (
          <img
            src={url}
            alt={label}
            draggable={false}
            className={cn(
              'max-h-full max-w-full object-contain transition-transform duration-150',
              zoom > 1 ? 'cursor-grab' : 'cursor-zoom-in',
            )}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            }}
            onDoubleClick={() => setZoomLevel(zoom > 1 ? 1 : 2.5)}
          />
        )}

        {total > 1 && (
          <>
            <EdgeButton side="left" label={t('photos.previous')} onClick={() => go(-1)}>
              <ChevronLeft />
            </EdgeButton>
            <EdgeButton side="right" label={t('photos.next')} onClick={() => go(1)}>
              <ChevronRight />
            </EdgeButton>
          </>
        )}

        {showInfo && <InfoPanel photo={photo} />}
      </div>

      <footer className="flex min-h-14 items-center gap-2 p-2">
        {editing ? (
          <>
            <Input
              autoFocus
              maxLength={300}
              value={caption}
              placeholder={t('photos.captionPlaceholder')}
              className="border-white/20 bg-white/10 text-white placeholder:text-white/40"
              onChange={(event) => setCaption(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                onSaveCaption?.(photo, caption);
                setCaption(null);
              }}
            />
            <Button
              type="button"
              size="sm"
              onClick={() => {
                onSaveCaption?.(photo, caption);
                setCaption(null);
              }}
            >
              {t('common.save')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/10"
              onClick={() => setCaption(null)}
            >
              {t('common.cancel')}
            </Button>
          </>
        ) : (
          <button
            type="button"
            disabled={!onSaveCaption || !mayModify}
            className="min-w-0 flex-1 truncate rounded px-2 py-1 text-left text-sm text-white/80 enabled:hover:bg-white/10"
            onClick={() => setCaption(photo.caption ?? '')}
          >
            {photo.caption ?? t('photos.captionAdd')}
          </button>
        )}
      </footer>
    </div>
  );
}

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      disabled={disabled}
      className="text-white/80 hover:bg-white/10 hover:text-white"
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function EdgeButton({
  side,
  label,
  onClick,
  children,
}: {
  side: 'left' | 'right';
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      className={cn(
        'absolute top-1/2 -translate-y-1/2 rounded-full bg-black/40 text-white hover:bg-black/60 hover:text-white',
        side === 'left' ? 'left-2' : 'right-2',
      )}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function InfoPanel({ photo }: { photo: LightboxPhoto }) {
  const { t } = useTranslation();
  const dimensions = formatDimensions(photo.width, photo.height);

  const rows: Array<[string, string | undefined]> = [
    [t('photos.infoFile'), photo.filename],
    [t('photos.infoTaken'), WHEN.format(new Date(photo.takenAt))],
    [t('photos.infoDimensions'), dimensions],
    [t('photos.infoSize'), formatSize(photo.size)],
    [
      t('photos.infoDuration'),
      photo.durationMs === undefined ? undefined : formatDuration(photo.durationMs),
    ],
    [t('photos.infoUploader'), photo.uploader?.name ?? photo.uploader?.email],
  ];

  return (
    <dl className="absolute bottom-2 left-2 max-w-[min(20rem,calc(100%-1rem))] rounded-lg bg-black/70 p-3 text-xs backdrop-blur">
      {rows
        .filter(([, value]) => Boolean(value))
        .map(([key, value]) => (
          <div key={key} className="flex gap-2 py-0.5">
            <dt className="w-24 shrink-0 text-white/50">{key}</dt>
            <dd className="min-w-0 wrap-break-word text-white/90">{value}</dd>
          </div>
        ))}
    </dl>
  );
}
